/**
 * Product Media Swiper Custom Element
 * Handles Swiper.js integration for product media galleries
 */

import { Component } from '@theme/component';

// Swiper is available as a global variable from swiper.js

class ProductMediaSwiper extends Component {
  constructor() {
    super();
    this.swiper = null;
    this.settings = this.getSettings();
  }

  connectedCallback() {
    super.connectedCallback();
    this.newSlide = this.querySelector('.swiper-slide.key-features-slide');
    // Add a fallback check in case swiper gets broken
    this.setupHealthCheck();

    this.initializeSwiper();
    if (window.innerWidth > 749) {
      if (this.newSlide) {
        if (this.newSlide && this.newSlide.parentNode) {
          this.newSlide.parentNode.removeChild(this.newSlide);
        }
        this.initializeSwiper();
      }
    }
  }

  /**
   * Setup a health check to detect if swiper breaks and recover
   */
  setupHealthCheck() {
    // Check every 2 seconds if swiper is still functional
    this.healthCheckInterval = setInterval(() => {
      const container = this.querySelector('.swiper-container');
      const slides = container?.querySelectorAll('.swiper-slide');

      // If we have slides but no swiper instance, something went wrong
      if (slides && slides.length > 0 && !this.swiper) {
        console.warn('Swiper health check failed - attempting recovery');
        this.settings = this.getSettings();
        this.initializeSwiper();
      }
    }, 2000);
  }

  disconnectedCallback() {
    // Clean up health check
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    super.disconnectedCallback();
  }

  /**
   * Called when element is updated via morphing (e.g., variant changes)
   * This ensures swiper reinitializes after section re-rendering
   */
  updatedCallback() {
    // Check if swiper container still exists after morphing
    const container = this.querySelector('.swiper-container');
    if (!container) {
      console.error('Swiper container missing after morph - skipping reinitialization');
      return;
    }

    // Check if we have slides
    const slides = container.querySelectorAll('.swiper-slide');
    if (!slides || slides.length === 0) {
      console.error('No swiper slides found after morph');
      return;
    }

    console.log(`Reinitializing swiper with ${slides.length} slides`);

    // Destroy existing swiper if it exists
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    // Wait for DOM to settle before reinitializing
    requestAnimationFrame(() => {
      // Double-check container still exists
      if (!this.querySelector('.swiper-container')) {
        console.error('Swiper container disappeared during async operation');
        return;
      }

      // Reinitialize with updated settings
      this.settings = this.getSettings();
      this.initializeSwiper();
    });
  }

  /**
   * Get settings from data attributes
   */
  getSettings() {
    return {
      // Carousel settings
      autoplay: this.dataset.autoplay === 'true',
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed) || 4000,
      slidesPerViewDesktop: parseInt(this.dataset.slidesPerViewDesktop) || 3,
      slidesPerViewMobile: parseInt(this.dataset.slidesPerViewMobile) || 1,
      spaceBetween: parseInt(this.dataset.spaceBetween) || 12,
      loop: this.dataset.loop !== 'false',

      // Navigation settings
      showArrows: this.dataset.showArrows !== 'false',
      showPagination: this.dataset.showPagination !== 'false',
      paginationStyle: this.dataset.paginationStyle || 'dots',

      // Animation settings
      speed: parseInt(this.dataset.speed) || 500,
      effect: this.dataset.effect || 'slide',

      // Responsive settings
      mobileBreakpoint: parseInt(this.dataset.mobileBreakpoint) || 750,

      // Accessibility
      enableA11y: this.dataset.enableA11y !== 'false'
    };
  }

  /**
   * Initialize Swiper instance
   */
  initializeSwiper() {
    // Swiper is available as a global variable from swiper.js
    if (typeof window.Swiper === 'undefined') {
      console.warn('Swiper not loaded. Retrying...');
      setTimeout(() => this.initializeSwiper(), 100);
      return;
    }

    this.createSwiper();
  }

  /**
   * Create Swiper instance with settings
   */
  createSwiper() {
    const swiperContainer = this.querySelector('.swiper-container');
    if (!swiperContainer) {
      console.error('Swiper container not found');
      return;
    }

    const config = this.getSwiperConfig();

    try {
      // Wait for next tick to ensure DOM is ready
      requestAnimationFrame(() => {
        this.swiper = new window.Swiper(swiperContainer, config);

        // Force swiper to recalculate breakpoints and dimensions
        setTimeout(() => {
          if (this.swiper && this.swiper.update) {
            this.swiper.update();
          }
        }, 50);

        this.setupEventListeners();
      });
    } catch (error) {
      console.error('Failed to initialize Swiper:', error);
    }
  }

  /**
   * Get Swiper configuration based on settings
   */
  getSwiperConfig() {
    const { settings } = this;

    // Determine effect based on screen size: fade for mobile (<=749px), slide for desktop (>=750px)
    const isMobile = window.innerWidth <= 749;
    const effect = isMobile ? 'fade' : 'slide';

    const config = {
      // Basic settings
      slidesPerView: settings.slidesPerViewMobile,
      spaceBetween: 0,
      //   spaceBetween: settings.spaceBetween,
      // loop: settings.loop,
      loop: false,
      // loopFillGroupWithBlank: true,
      speed: settings.speed,
      effect: effect,
      adaptiveHeight: false,
      autoHeight: false,

      // Observer to handle DOM changes (important for morphing)
      observer: true,
      observeParents: true,
      observeSlideChildren: true,

      // Responsive breakpoints
      breakpoints: {
        [settings.mobileBreakpoint]: {
          slidesPerView: settings.slidesPerViewDesktop,
          spaceBetween: 0,
          loopFillGroupWithBlank: true,
          loop: true,
          effect: 'slide', // Ensure slide effect at desktop breakpoint
        }
      },

      // Autoplay
      ...(settings.autoplay && {
        autoplay: {
          delay: settings.autoplaySpeed,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }
      }),

      // Navigation
      ...(settings.showArrows && {
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }
      }),

      // Pagination
      ...(settings.showPagination && {
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          dynamicBullets: false,
          renderBullet: settings.paginationStyle === 'counter' ? this.renderCounterBullet.bind(this) : undefined,
        }
      }),

      // Accessibility
      ...(settings.enableA11y && {
        a11y: {
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
          firstSlideMessage: 'This is the first slide',
          lastSlideMessage: 'This is the last slide',
        }
      }),

      // Events
      on: {
        init: this.onSwiperInit.bind(this),
        slideChange: this.onSlideChange.bind(this),
        autoplayStart: this.onAutoplayStart.bind(this),
        autoplayStop: this.onAutoplayStop.bind(this),
        afterInit: this.onSwiperAfterInit.bind(this),
        slideChangeTransitionStart: () => {
          const prev = this.swiper.slides[this.swiper.previousIndex];
          const next = this.swiper.slides[this.swiper.activeIndex];

          if (this.swiper.activeIndex > this.swiper.previousIndex) {
            prev.classList.add('clipping-out-left');
          } else {
            prev.classList.add('clipping-out-right');
          }
        },

        slideChangeTransitionEnd: () => {
          this.swiper.slides.forEach((slide) => {
            slide.classList.remove('clipping-out-left', 'clipping-out-right');
          });
        },
      }

    };

    return config;
  }

  /**
   * Custom bullet renderer for counter pagination
   */
  renderCounterBullet(index, className) {
    const total = this.swiper?.slides?.length || 0;
    return `<span class="${className}">${index + 1}/${total}</span>`;
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Pause autoplay on hover
    this.addEventListener('mouseenter', () => {
      if (this.swiper && this.swiper.autoplay) {
        this.swiper.autoplay.pause();
      }
    });

    this.addEventListener('mouseleave', () => {
      if (this.swiper && this.swiper.autoplay) {
        this.swiper.autoplay.resume();
      }
    });

    // Handle lookbook button click
    this.setupLookbookButton();

    // Handle resize - update effect if needed
    window.addEventListener('resize', this.debounce(() => {
      if (this.swiper) {
        const currentWidth = window.innerWidth;
        const shouldBeFade = currentWidth <= 749;
        const currentEffect = this.swiper.params.effect;
        const shouldBeSlide = currentWidth >= 750;

        // If effect needs to change, reinitialize Swiper
        if ((shouldBeFade && currentEffect !== 'fade') || (shouldBeSlide && currentEffect !== 'slide')) {
          // Destroy and recreate with new effect
          this.swiper.destroy(true, true);
          this.swiper = null;
          this.initializeSwiper();
          return;
        }

        this.swiper.update();
      }
    }, 250));
  }
  /**
    * Setup lookbook button click handler
    */
  setupLookbookButton() {
    const lookbookBtn = this.querySelector('#lookbook-btn');
    if (lookbookBtn) {
      lookbookBtn.addEventListener('click', () => {
        this.scrollToLookbookSection();
      });
    }
  }

  /**
   * Scroll to lookbook section with 100px margin from top
   */
  scrollToLookbookSection() {
    // Find lookbook section - try common selectors

    let lookbookSection = document.querySelector('.lookbook-gallery-section');

    if (lookbookSection) {
      const offsetTop = lookbookSection.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    } else {
      console.warn('Lookbook section not found');
    }
  }
  /**
   * Swiper event handlers
   */
  onSwiperInit() {
    console.log('Product Media Swiper initialized');

    // Force update to ensure breakpoints are applied correctly
    if (this.swiper && this.swiper.update) {
      this.swiper.update();
    }

    this.dispatchEvent(new CustomEvent('swiper:init', {
      detail: { swiper: this.swiper }
    }));
  }

  onSwiperAfterInit() {
    console.log('Product Media Swiper fully initialized');

    // Final update to ensure everything is in sync
    setTimeout(() => {
      if (this.swiper && this.swiper.update) {
        this.swiper.update();
      }
    }, 100);

    this.dispatchEvent(new CustomEvent('swiper:afterInit', {
      detail: { swiper: this.swiper }
    }));
  }

  onSlideChange() {
    // Check if swiper is properly initialized before accessing properties
    if (!this.swiper || typeof this.swiper.activeIndex === 'undefined') {
      return;
    }

    this.dispatchEvent(new CustomEvent('swiper:slideChange', {
      detail: {
        swiper: this.swiper,
        activeIndex: this.swiper.activeIndex,
        realIndex: this.swiper.realIndex
      }
    }));
  }

  onAutoplayStart() {
    if (!this.swiper) return;

    this.dispatchEvent(new CustomEvent('swiper:autoplayStart', {
      detail: { swiper: this.swiper }
    }));
  }

  onAutoplayStop() {
    if (!this.swiper) return;

    this.dispatchEvent(new CustomEvent('swiper:autoplayStop', {
      detail: { swiper: this.swiper }
    }));
  }

  /**
   * Public methods
   */
  nextSlide() {
    if (this.swiper && this.swiper.slideNext) {
      this.swiper.slideNext();
    }
  }

  prevSlide() {
    if (this.swiper && this.swiper.slidePrev) {
      this.swiper.slidePrev();
    }
  }

  goToSlide(index) {
    if (this.swiper && this.swiper.slideTo) {
      this.swiper.slideTo(index);
    }
  }

  startAutoplay() {
    if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.start) {
      this.swiper.autoplay.start();
    }
  }

  stopAutoplay() {
    if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.stop) {
      this.swiper.autoplay.stop();
    }
  }

  updateSwiper() {
    if (this.swiper && this.swiper.update) {
      this.swiper.update();
    }
  }

  /**
   * Check if swiper is ready
   */
  isReady() {
    return this.swiper && typeof this.swiper.activeIndex !== 'undefined';
  }

  /**
   * Utility function for debouncing
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Update settings and reinitialize
   */
  updateSettings(newSettings) {
    this.settings = { ...this.settings, ...newSettings };
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.createSwiper();
    }
  }
}

// Register the custom element
customElements.define('product-media-swiper', ProductMediaSwiper);

// Export for module usage
export default ProductMediaSwiper;
